import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IValidationState, SaveStatus } from './types';
import {
  BookingBaseType,
  BookingMethods,
  bookingStatusType,
  BookingType,
  DiaryMobileVersion,
  IBookedByValidation,
  IBooking,
  IBookingFromPouch,
  ICustomer,
  JOIN_PREFIX,
  preAuthStatus,
  servicePaymentType,
  BookingStatusNames,
  CustomerTypes,
} from 'shared-types/index';
import { DiaryRootState } from 'appRoot/rootReducer';
import {
  BookingCreatorPanelType,
  BookingValidationItems,
  IValidationErrorListItem,
  ValidationErrorType,
} from 'external-services/booking-validation-service/bookingValidation.types';
import BookingValidationService from 'external-services/booking-validation-service';
import { BookingMode } from 'booking-creator/reducers/display/types';
import {
  addPendingPaymentStatusToBooking,
  checkCustomerValidationHelper,
  checkForInlineCustomer,
  checkForWalkinCustomerDetail,
  getOptionsValidationMessages,
  getPaymentMessage,
  getRecurringBookingMessage,
  moveBookingToAnotherDate,
  normaliseOptionsForSave,
  normaliseTagsForSave,
  saveValidationCheck,
  setNonStandbyBookingType,
} from './helper';
import { cloneDeep, isEmpty, isEqual } from 'lodash-es';
import { StatusServiceInstance } from 'external-services/status-service';
import BookingUpdateService from 'external-services/booking-update-service';
import StaffService from 'external-services/staff-service/staff.service';
import TableJoinService from 'booking-creator/services/tableJoin/tableJoin.service';
import RecurringBookingService from 'external-services/recurring-booking-service';
import BookingNotificationsService from 'external-services/booking-notification-service/bookingNotifications.service';
import { paymentActions, setPaymentUrl } from 'booking-creator/reducers/payment/slice';
import { displayActions } from 'booking-creator/reducers/display/slice';
import BookingNormaliseService from 'external-services/booking-normalise-service/bookingNormalise.service';
import statusReducer, { getStatusByName } from 'app/reducers/status/slice';
import DateUtilsService from 'external-services/date-utils-service';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import BookingLookupService from 'external-services/booking-lookup-service';
import BookingDiagnosticService from 'booking-creator/services/booking-diagnostic-service';
import CompareObjectsService, {
  IChangeDetails,
} from 'external-services/compare-objects-service/CompareObjects';
import { checkDuplicatedCustomer } from 'booking-creator/reducers/customer/slice';
import { scrollPositionActions } from 'booking-creator/reducers/scrollPosition/slice';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
import GeneralSettingsService from 'external-services/general-settings-service/generalSettings.service';
import TagsService from 'external-services/tags-service';

const NS = 'validationSlice';

export const SBL_EMAIL_VALIDATION_MESSAGE: string = 'Email is mandatory for Standby List';
export const SBL_SMSVALIDATION_MESSAGE: string = 'Phone number is mandatory for Standby List';

export const initialState: IValidationState = {
  originalBooking: {} as IBooking, // if mode === BookingMode.BookingMode this can be used as the moved booking data
  canSave: false,
  cantSaveMessageList: [],
  staffNewBooking: {
    isMandatory: false,
    isEnabled: false,
  },
  staffEditBooking: {
    isMandatory: false,
    isEnabled: false,
  },
  staffValue: '',
  saveStatus: SaveStatus.Fresh,
  saveAttempted: false,
  tableSeaterFullyLoaded: false,
  paymentEmail: '',
  paymentPhone: '',
  isPaymentPhoneUpdateChecked: false,
  isPaymentEmailUpdateChecked: false,
  validPhoneMessage: '',
  isPaymentEmailValid: true,
  isPaymentPhoneValid: true,
  emailErrorMsg: '',
  isBookedByValid: true, // treat as true if not enabled
  bookedByErrors: [],
  isBookedByEmailValid: true, // treat as true if no input yet
  isBookedByPhoneValid: true, // treat as true if no input yet
  bookedByPaymentEmail: '', // for payment link
  bookedByPaymentPhone: '', // for payment link
  isBookedByPaymentEmailValid: true, // for payment link
  isBookedByPaymentPhoneValid: true, // for payment link
  isSavingBookedByPhone: false, // for payment link
  isSavingBookedByEmail: false, // for payment link
  bookedByPaymentEmailErrorMsg: '', // for payment link
  bookedByPaymentPhoneErrorMsg: '', // for payment link
  isSavePaymentEmailToCustomer: false, // for payment link
  isSavePaymentPhoneNumberToCustomer: false, // for payment link
  saveSeatAttempted: false,
  saveNewWalkinAttempted: false,
  isConfirmedSeat: false,
  isConfirmedNewWalkinCustomer: false,
  isBookingExternallyUpdated: false,
};

function trackMoveBooking(
  originalBooking: Partial<IBooking>,
  savableBooking: IBooking,
  savedBooking: IBooking | IBookingFromPouch,
  state: DiaryRootState
) {
  const { activeBooking, mode, isManualCopyOriginalBooking } = state.displayReducer;
  const { id, name, timeZoneId } = state.venueReducer;
  const diagnosticPayload = {
    message: 'move_booking_to_another_date',
    level: 'info',
    venueId: id,
    venueName: name,
    venueTimeZoneId: timeZoneId,
    originalBookingId: originalBooking?._id,
    savableBookingId: savableBooking?._id,
    savedBookingId: savedBooking?._id,
    activeBookingId: activeBooking?._id,
    savableMatchesOriginal: savableBooking?._id === originalBooking?._id,
    originalMatchesActive: originalBooking?._id === activeBooking?._id,
    savableMatchesActive: savableBooking?._id === activeBooking?._id,

    originalTime: String(originalBooking?.time),
    originalTimeIsDate: originalBooking?.time instanceof Date,
    savableTime: String(savableBooking?.time),
    savableTimeIsDate: savableBooking?.time instanceof Date,

    originalCustomerId: originalBooking?.customerId,
    savableCustomerId: savableBooking?.customerId,
    savedCustomerId: savedBooking?.customerId,
    savedMatchesSavableCustomer: savedBooking?.customerId === savableBooking?.customerId,
    savedMatchesOriginalCustomer: savedBooking?.customerId === originalBooking?.customerId,
    savedMovedFromBookingId: savedBooking?.movedFromBookingId,
    movedFromMatchesOriginal: savedBooking?.movedFromBookingId === originalBooking?._id,
    mode,
    isManualCopyOriginalBooking,
  };
  if (savableBooking?._id === originalBooking?._id) {
    if (savableBooking?.customerId !== originalBooking?.customerId) {
      diagnosticPayload.message = 'change_customer';
      diagnosticPayload.level = 'warn';
    } else if (
      !(originalBooking?.time instanceof Date) ||
      !(savableBooking?.time instanceof Date)
    ) {
      diagnosticPayload.message = 'type_mismatch';
      diagnosticPayload.level = 'warn';
    }
  } else {
    diagnosticPayload.message = 'stale_original_booking';
    diagnosticPayload.level = 'error';
  }
  console.error(
    NS,
    'saveEditBooking: taking "move to another date" path — diagnostic snapshot',
    diagnosticPayload
  );
  BookingDiagnosticService.reportUnexpectedDateChange(diagnosticPayload);
}

/**
 * Only use when mode equals editBooking (not for newBooking)
 */
export const saveEditBooking = createAsyncThunk(
  'validation/saveEditBooking',
  async (
    payload: {
      newStatusName?: string;
      waitList?: boolean;
      seatNowFromViewMode?: boolean;
    },
    { dispatch, getState }
  ): Promise<SaveStatus> => {
    const state = getState() as DiaryRootState;
    const {
      displayReducer: { mode, activeBooking, originalBooking, activeSchedule },
      customerReducer: { customer, originalCustomer },
      validationReducer: {
        staffValue,
        isBookedByPaymentEmailValid,
        isBookedByPaymentPhoneValid,
        bookedByPaymentEmail,
        bookedByPaymentPhone,
        isSavingBookedByEmail,
        isSavingBookedByPhone,
        paymentPhone,
        paymentEmail,
        isPaymentPhoneValid,
        isPaymentEmailValid,
        isSavePaymentEmailToCustomer,
        isSavePaymentPhoneNumberToCustomer,
      },
      bookingOptionsUpsellReducer: { allBookingOptions },
      communicationReducer: { emailEnabled, smsEnabled },
      statusReducer: { statusOptions },
    } = state;

    // const newStatusName = ''; // @todo: possibly needed for waitlist walkins and SBL
    // { newStatusName: string; closeDialogOnSuccess: boolean }

    // prettier-ignore
    if (mode !== BookingMode.editBooking && !payload.seatNowFromViewMode) {
      console.warn(NS, `"saveEditBooking" failed to save because the mode was ${mode}.`);
      return SaveStatus.ValidationFailed;
    }
    dispatch(actions.setSaveStatus(SaveStatus.SaveInProgress));

    let saveState = SaveStatus.Pending;

    let savableBooking: IBooking = cloneDeep(activeBooking) as IBooking; // clone it so we can extend it
    // for payment link - booked-by
    if (activeBooking.isBookedBy) {
      if (isSavingBookedByEmail && isBookedByPaymentEmailValid) {
        savableBooking.bookedBy.email = bookedByPaymentEmail;
      }
      if (isSavingBookedByPhone && isBookedByPaymentPhoneValid) {
        savableBooking.bookedBy.phone = bookedByPaymentPhone;
      }
    }
    //update customer email and phone if they are valid and the "send and save" checkbox is checked in payment link section
    if (isSavePaymentEmailToCustomer && isPaymentEmailValid) {
      savableBooking.email = paymentEmail;
    }
    if (isSavePaymentPhoneNumberToCustomer && isPaymentPhoneValid) {
      savableBooking.phone = paymentPhone;
    }
    const paymentDetails = activeBooking.paymentDetails;
    const result = (await dispatch(
      getStatusByName({
        newStatusName: payload?.newStatusName,
        oldStatusName: activeBooking.status?.name,
        activeBookingPaymentType: paymentDetails?.paymentType,
        orgBooking: originalBooking as IBooking,
      })
    )) as any;

    const newStatus = result.payload?.newStatus;
    const hasChanged = result.payload?.hasChanged;

    if (newStatus) {
      savableBooking.status = newStatus;
    }
    if (hasChanged) {
      // adjusts durations for finished and seated statuses
      savableBooking = BookingUpdateService.updateAfterStatusChange(
        savableBooking,
        originalBooking as IBooking
      );
    }

    setNonStandbyBookingType(savableBooking);

    /**
     * remove tables for standbylist
     */
    BookingNormaliseService.removeTablesStandbyBookingType(savableBooking);

    // @todo:include showUpdatePaymentLinkInfoConfirmationDialog when payment link component has been added

    /**
     * @todo: didn't include PhoneNumberService.resetPhoneNumberWithTypeValidation because it made no sense in
     * Diary (passing in booking instead of customer). Once Diary has been investigated, come back to this.
     */
    const tempCustomer = cloneDeep(customer);
    const tempOriginalCustomer = savableBooking.customerId ? originalCustomer : null;

    const forceUpdateCustomer = !CompareObjectsService.hasIdenticalData(customer, originalCustomer);
    //update customer email and phone if they are valid and the "send and save" checkbox is checked in payment link section
    if (isSavePaymentEmailToCustomer && isPaymentEmailValid) {
      tempCustomer.email = paymentEmail;
    }
    if (isSavePaymentPhoneNumberToCustomer && isPaymentPhoneValid) {
      tempCustomer.phone = paymentPhone;
    }

    // @todo: need to remove last name for walkin which has no valid customer. (Horita)

    // @todo: include updateCustomerWithPaymentCustomerDetails() when payment link component has been added

    // using the first selectedTableId because if using the layout-based table join, which is prefixed with 'join_', there can only be one selected.
    const firstSelectedTable = savableBooking.tables[0];
    if (firstSelectedTable && firstSelectedTable._id.indexOf(JOIN_PREFIX) === 0) {
      const isManualJoin = false;
      // back end has no notion of a table join object, so must convert it to multiple tables before saving
      savableBooking.tables = TableJoinService.convertToManualJoin(
        [firstSelectedTable._id],
        savableBooking.tables,
        isManualJoin
      );
    }
    normaliseOptionsForSave(savableBooking, allBookingOptions);

    let savedBooking: IBooking | IBookingFromPouch;
    // trigger pre-auth dialog if booking is cancelled and payment is pre-auth and pre-auth status is holding
    if (
      savableBooking?.status?.statusType === bookingStatusType.cancelled &&
      originalBooking?.status?.statusType !== bookingStatusType.cancelled &&
      savableBooking?.paymentDetails?.paymentType === servicePaymentType.preAuth &&
      savableBooking?.paymentSummary?.preAuthStatus === preAuthStatus.holding
    ) {
      dispatch(paymentActions.setPreAuthDialog(true));
      dispatch(paymentActions.setIsSetActiveBookingInBookingCreator(false));
    } else {
      dispatch(paymentActions.setPreAuthDialog(false));
      dispatch(paymentActions.setIsSetActiveBookingInBookingCreator(true));
    }
    if (DateUtilsService.isOnSameDate(originalBooking.time, savableBooking.time)) {
      try {
        savedBooking = await BookingUpdateService.updateFromEditBookingSave(
          savableBooking,
          originalBooking as IBooking,
          tempCustomer,
          tempOriginalCustomer,
          emailEnabled,
          smsEnabled,
          staffValue,
          activeSchedule,
          null,
          false,
          forceUpdateCustomer
        );
        // Generate PaymentURL
        dispatch(setPaymentUrl(savedBooking));
      } catch (err: any) {
        // for fix update conflicts
        if (err.status === 409) {
          // get newest booking._rev, then retry
          const newestBooking = await BookingLookupService.getById(
            savableBooking._id,
            savableBooking.time
          );
          const ignoreList = [
            'sectionLayouts',
            'selectedLayouts',
            'staffName',
            '_rev',
            'customersCovid',
            'lastModifiedDate',
            'functionTracker',
            'bookingEnd',
          ];
          const changedValues: IChangeDetails[] = CompareObjectsService.computeObjectDifference(
            savableBooking,
            newestBooking,
            '',
            true,
            ignoreList
          );
          let errorContent = `PropertyName: ConflictedBookingDetail -> YourBookingDetail \n`;
          changedValues.forEach((change) => {
            errorContent += `${change.property}: ${change.originalValue} -> ${change.newValue} \n`;
          });
          dispatch(
            errorStateActions.setErrorState({
              errorTitle: 'Save booking conflict!.',
              errorMessage: `Sorry, this booking is being edited on another device. Please refresh the page and try again.`,
              accordionDetail: errorContent,
            })
          );
          saveState = SaveStatus.SaveFailed;
        } else {
          // other failing or retry more than 3 time. should stop retry and throw error
          saveState = SaveStatus.SaveFailed;
          console.warn(NS, 'updateFromEditBookingSave error', err);
          dispatch(
            errorStateActions.setErrorState({
              errorTitle: 'Save booking  error',
              errorMessage: `There was an error while saving the booking. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
              accordionDetail: getAccordionDetail(err),
            })
          );
        }
      }
    } else {
      const tempSavableBooking = cloneDeep(savableBooking);
      const result = await moveBookingToAnotherDate(
        savableBooking,
        originalBooking as IBooking,
        tempCustomer,
        activeSchedule,
        statusOptions,
        staffValue,
        forceUpdateCustomer
      );

      savedBooking = result.savedBooking;
      saveState = result.saveState;
      trackMoveBooking(
        originalBooking,
        tempSavableBooking,
        savedBooking,
        getState() as DiaryRootState
      );
    }

    if (saveState === SaveStatus.Pending && savedBooking) {
      saveState = SaveStatus.SaveSuccessful;
    }

    return saveState;
  }
);

/**
 * Only use when mode equals newBooking (not for editBooking)
 */
export const saveNewBooking = createAsyncThunk(
  'validation/saveNewBooking',
  async (payload: void, { dispatch, getState }): Promise<SaveStatus> => {
    const state = getState() as DiaryRootState;
    const {
      displayReducer: { mode, activeBooking, activeSchedule },
      bookingOptionsUpsellReducer: { allBookingOptions },
      tagsReducer: { allTags },
      customerReducer: {
        customer,
        skipCustomerDuplicationCheck,
        originalCustomer,
        forceUpdateCustomer,
      },
      validationReducer: { staffValue },
      recurringBooking,
      statusReducer: { fullStatusOptions },
      venueReducer: venue,
    } = state;

    const { enableDiaryMobilePlusRestricted, timeZoneId } = state.venueReducer;
    // prettier-ignore
    const isPlusVersion = state.settingReducer.mobileVersion === DiaryMobileVersion.PlusVersion;
    // prettier-ignore
    const isRestrictedVersion = state.settingReducer.mobileVersion === DiaryMobileVersion.PlusVersion && enableDiaryMobilePlusRestricted;

    const settings = GeneralSettingsService.getSettingsCache();
    const isAutoConfirm = settings?.autoConfirm;

    // prettier-ignore
    if (mode !== BookingMode.newBooking) {
            console.warn(NS, `"saveNewBooking" failed to save because the mode was ${mode}.`)
            return SaveStatus.ValidationFailed;
        }

    dispatch(actions.setSaveStatus(SaveStatus.SaveInProgress));

    const savableBooking: IBooking = cloneDeep(activeBooking) as IBooking; // clone it so we can extend it
    // for auto confirm booking on today
    if (
      isAutoConfirm &&
      DateUtilsService.isDateToday(activeBooking.time, timeZoneId) &&
      savableBooking.bookingType !== BookingType.StandbyList
    ) {
      savableBooking.status = fullStatusOptions.find(
        (s) => s.name === BookingStatusNames.Confirmed
      );
    }
    const customerCloned: ICustomer = cloneDeep(customer); // needs to be cloned because _id may get dynamically added
    checkForInlineCustomer(customerCloned);
    checkForWalkinCustomerDetail(customerCloned, savableBooking);

    const saveState = saveValidationCheck(state);

    if (saveState === SaveStatus.ValidationSuccessful) {
      normaliseOptionsForSave(savableBooking, allBookingOptions);

      normaliseTagsForSave(savableBooking, allTags);

      savableBooking.staffName = staffValue;

      if (savableBooking.method === BookingMethods.walkin) {
        StatusServiceInstance.forceSeatedWalkinToSeatedStatus(savableBooking);
      }

      addPendingPaymentStatusToBooking(savableBooking);

      const isRecurringBooking =
        !isEmpty(recurringBooking?.bookingList) && !!recurringBooking?.requestParameters;

      savableBooking.type = BookingBaseType.booking;

      savableBooking.customersCovid = [];
      if (!savableBooking.bookingLinks) {
        savableBooking.bookingLinks = [];
      }
      if (!savableBooking.additionalPaymentDetails) {
        savableBooking.additionalPaymentDetails = [];
      }
      if (!savableBooking.selectedMenuOptions) {
        savableBooking.selectedMenuOptions = [];
      }
      if (!savableBooking.isManualJoin) {
        savableBooking.isManualJoin = false;
      }
      if (savableBooking.isBookedBy) {
        TagsService.addBookedByTag(savableBooking.tags);
      }
      if (
        !skipCustomerDuplicationCheck &&
        isPlusVersion &&
        savableBooking.method !== BookingMethods.walkin
      ) {
        const res = await dispatch(checkDuplicatedCustomer());
        // @ts-ignore
        if (res.payload.duplicateCustomer) {
          return saveState;
        }
      }

      let successfullySavedBooking: IBookingFromPouch;

      try {
        const forceUpdateForCustomerChange = !CompareObjectsService.hasIdenticalData(
          customer,
          originalCustomer
        );

        /* forceUpdateCustomer property in BookingUpdateService.add() is used to force update customer
           forceUpdateForCustomerChange checks if the customer value has changed
           forceUpdateCustomer is used to force update customer when the customer is not changed but the customer from another venue is getting added.
        */

        successfullySavedBooking = await BookingUpdateService.add(
          savableBooking,
          customerCloned,
          null,
          activeSchedule,
          isRecurringBooking,
          forceUpdateCustomer || forceUpdateForCustomerChange
        );
      } catch (err) {
        console.warn(NS, 'saveNewBooking', err);
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Save booking error',
            errorMessage: `Sorry, there was an error while saving the booking tags. Please try again. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(err),
          })
        );
        return SaveStatus.SaveFailed;
      }

      // Generate PaymentURL
      dispatch(setPaymentUrl(successfullySavedBooking));

      const activeBooking = BookingNormaliseService.normalizeModelToView(
        cloneDeep(successfullySavedBooking)
      );
      dispatch(displayActions.setActiveBooking(activeBooking));

      if (isRecurringBooking) {
        try {
          const requestParameters = cloneDeep(recurringBooking.requestParameters); // needs to be modified to have to clone it
          requestParameters.bookings = RecurringBookingService.updateRecurringBookingsProps(
            requestParameters.bookings,
            savableBooking
          );
          RecurringBookingService.updateRecurringBookingsCustomer(
            requestParameters.recurringBooking,
            customerCloned,
            successfullySavedBooking.customerId // uses the customer id from the saved booking, so new customers get one
          );
          console.log('requestParameters', requestParameters);
          await RecurringBookingService.SaveRecurringBooking(requestParameters);
          await BookingNotificationsService.sendNotification(
            successfullySavedBooking,
            BookingNotificationsService.NEW
          );
        } catch (err) {
          console.warn(NS, 'recurring failed', err);
          dispatch(
            errorStateActions.setErrorState({
              errorTitle: 'Recurring booking error',
              errorMessage: `There was an error while updating recurring bookings. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
              accordionDetail: getAccordionDetail(err),
            })
          );
        }
      }

      return SaveStatus.SaveSuccessful;
    }

    return saveState;
  }
);

export const updateCanSaveNewBooking = createAsyncThunk(
  'validation/updateCanSaveNewBooking',
  async (
    payload: void,
    { dispatch, getState }
  ): Promise<{ canSave: boolean; cantSaveMessageList: IValidationErrorListItem[] }> => {
    const state: DiaryRootState = getState() as DiaryRootState;
    const {
      displayReducer,
      bookingTimesReducer,
      venueReducer,
      validationReducer,
      settingReducer,
      staffValidationReducer,
    } = state;
    const { mobileVersion } = settingReducer;
    const { staffValue, staffNewBooking } = validationReducer;

    const isPlusVersion = mobileVersion === DiaryMobileVersion.PlusVersion;
    const mode = displayReducer.mode;
    let activeBooking: IBooking = displayReducer.activeBooking as IBooking;
    const selectedInfoAtTime = bookingTimesReducer.selectedInfoAtTime;
    const skipDefaultSelection = bookingTimesReducer.skipDefaultSelection;
    const isWalkin = activeBooking.method === BookingMethods.walkin;
    const isStandByListEnabled = venueReducer.standByList && !isWalkin;

    // if staff name for new bookings is required, but not populated
    const includeStaffCheck =
      StaffService.getNewBookingValidation(
        staffValidationReducer,
        isWalkin ? 'creatingWalkin' : 'creatingBooking',
        isWalkin
      ) && !staffValue;

    let validationList: BookingValidationItems[] = [];

    let cantSaveMessageList: IValidationErrorListItem[] = [];

    // for new booking if default selection is true then show the time validation - skipDefaultSelection becomes false once time pill is clicked again
    if (skipDefaultSelection) {
      activeBooking = { ...activeBooking, time: null };
    }

    const detailsValid = BookingValidationService.getBookingDetailsValidation(
      activeBooking,
      selectedInfoAtTime,
      isStandByListEnabled,
      includeStaffCheck,
      validationList,
      true
    );

    const validationHelperResult = checkCustomerValidationHelper(state, true);
    if (!validationHelperResult.isValid) {
      cantSaveMessageList = cantSaveMessageList.concat(validationHelperResult.cantSaveMessageList);
    }

    // PaymentValid should be true if bookingType is waitlist/walkin
    const paymentValid =
      activeBooking.bookingType === BookingType.WaitList ||
      activeBooking.method === BookingMethods.walkin
        ? true
        : BookingValidationService.isBookingPaymentValid(activeBooking.paymentDetails);
    const guestListValid = true; // covid guest list not needed Diary Mobile version
    const bookingToMoveDateTime: Date = null; // @todo: do we need this when changing dates in existing booking?

    let canSave = BookingValidationService.canSaveNewBookings({
      detailsValid,
      customerValid: validationHelperResult.isValid,
      paymentValid,
      guestListValid,
      bookingDateTime: activeBooking.time,
      bookingToMoveDateTime,
    });

    const optionsValidationMessages = isPlusVersion
      ? getOptionsValidationMessages({
          bookingOptionsUpsellReducer: state.bookingOptionsUpsellReducer,
          bookingType: activeBooking.bookingType,
          method: activeBooking.method,
          isNewBooking: true,
        })
      : [];

    if (optionsValidationMessages.length) {
      canSave = false;
      validationList = [...validationList, ...optionsValidationMessages];
    }

    // for recurring booking
    const recurringBookingMessages = getRecurringBookingMessage({
      recurringBookingReducer: state.recurringBooking,
    });

    if (recurringBookingMessages.length) {
      canSave = false;
      validationList = [...validationList, ...recurringBookingMessages];
    }

    if (!activeBooking.time) {
      canSave = false;
      validationList = [...validationList, BookingValidationItems.selectedTime];
    }

    if (!canSave && validationList.length) {
      cantSaveMessageList = cantSaveMessageList.concat(
        BookingValidationService.getValidationMessages(validationList)
      );
    }

    const panels: BookingCreatorPanelType[] = [];
    cantSaveMessageList.forEach((item) => {
      !panels.some((panel) => panel === item.panel) && panels.push(item.panel);
    });
    dispatch(scrollPositionActions.setValidationErrorPanels(panels));
    return { canSave, cantSaveMessageList };
  }
);

export const updateCanSave = createAsyncThunk(
  'validation/updateCanSave',
  async (_, { dispatch, getState }): Promise<void> => {
    const state: DiaryRootState = getState() as DiaryRootState;
    const mode = state.displayReducer.mode;
    const isNewMode = mode === BookingMode.newBooking;
    const isEditMode = mode === BookingMode.editBooking;

    if (isNewMode) dispatch(updateCanSaveNewBooking());
    else if (isEditMode) dispatch(updateCanSaveEditBooking());
  }
);

export const checkIfIsStaffEditRequired = (state: DiaryRootState) => {
  const {
    displayReducer,
    staffValidationReducer: staffInitialSettings,
    customerReducer,
    venueReducer: { timeZoneId },
  } = state;
  const { customer, originalCustomer } = customerReducer;
  const { originalBooking, activeBooking } = displayReducer;
  const isWalkin = activeBooking.method === BookingMethods.walkin;
  const staffEnabled =
    staffInitialSettings?.staffEditEnabled &&
    staffInitialSettings?.staffEditRequired &&
    originalBooking.method !== BookingMethods.walkin;

  const isRequiredOnChangingDate =
    StaffService.getNewBookingValidation(staffInitialSettings, 'changingDate', isWalkin) &&
    !DateUtilsService.isDateInSpecificRange(activeBooking.time, originalBooking.time, timeZoneId);

  // if not valid any required setting
  return (
    !StaffService.staffNameNotRequired({
      staffEnabled,
      oldBooking: originalBooking as IBooking,
      newBooking: activeBooking as IBooking,
      orgCustomer: originalCustomer,
      updatedCustomer: customer,
      waitList: activeBooking.bookingType === BookingType.WaitList,
      staffEditOptions: staffInitialSettings.staffEdit,
    }) || isRequiredOnChangingDate
  );
};

export const updateCanSaveEditBooking = createAsyncThunk(
  'validation/updateCanSaveEditBooking',
  async (
    payload: void,
    { dispatch, getState }
  ): Promise<{ canSave: boolean; cantSaveMessageList: IValidationErrorListItem[] }> => {
    const state: DiaryRootState = getState() as DiaryRootState;
    const {
      displayReducer,
      bookingTimesReducer,
      venueReducer,
      validationReducer,
      settingReducer,
      bookingOptionsUpsellReducer,
      customerReducer,
    } = state;
    const { staffValue } = validationReducer;
    const { mobileVersion } = settingReducer;
    const { activeBooking } = displayReducer;
    const { enableBookingUpsellOptions, enableBookingOptions } = bookingOptionsUpsellReducer;
    const { customer } = customerReducer;

    // if not valid any required setting
    const isStaffEditRequired = checkIfIsStaffEditRequired(state);

    const isPlusVersion = mobileVersion === DiaryMobileVersion.PlusVersion;

    const includeStaffCheck = isStaffEditRequired && !staffValue;
    let validationList: BookingValidationItems[] = [];

    const selectedInfoAtTime = bookingTimesReducer.selectedInfoAtTime;
    const isStandByListEnabled =
      venueReducer.standByList && activeBooking.method !== BookingMethods.walkin;

    let cantSaveMessageList: IValidationErrorListItem[] = [];
    let canSave = BookingValidationService.getBookingDetailsValidation(
      activeBooking as IBooking,
      selectedInfoAtTime,
      isStandByListEnabled,
      includeStaffCheck,
      validationList,
      true
    );

    let optionsValidationMessages = isPlusVersion
      ? getOptionsValidationMessages({
          bookingOptionsUpsellReducer: state.bookingOptionsUpsellReducer,
        })
      : [];

    if (!enableBookingUpsellOptions && !enableBookingOptions) {
      optionsValidationMessages = [];
    }

    if (optionsValidationMessages.length) {
      canSave = false;
      validationList = [...validationList, ...optionsValidationMessages];
    }

    // for recurring booking
    const recurringBookingMessages = getRecurringBookingMessage({
      recurringBookingReducer: state.recurringBooking,
    });

    if (recurringBookingMessages.length) {
      canSave = false;
      validationList = [...validationList, ...recurringBookingMessages];
    }

    if (!canSave && validationList.length) {
      cantSaveMessageList = cantSaveMessageList.concat(
        BookingValidationService.getValidationMessages(validationList)
      );
    }

    const paymentMessage = getPaymentMessage(
      activeBooking.paymentSummary,
      activeBooking.paymentDetails,
      customer.customerType === CustomerTypes.inline ? isEmpty(activeBooking.bookedBy) : false
    );
    if (paymentMessage) {
      canSave = false;
      cantSaveMessageList.push({
        msg: paymentMessage,
        type: ValidationErrorType.payment,
        panel: BookingCreatorPanelType.payment,
      });
    }

    const validationHelperResult: {
      isValid: boolean;
      cantSaveMessageList: IValidationErrorListItem[];
    } = checkCustomerValidationHelper(state);

    if (!validationHelperResult.isValid) {
      cantSaveMessageList = cantSaveMessageList.concat(validationHelperResult.cantSaveMessageList);
      canSave = false;
    }

    const panels: BookingCreatorPanelType[] = [];
    cantSaveMessageList.forEach((item) => {
      !panels.some((panel) => panel === item.panel) && panels.push(item.panel);
    });
    dispatch(scrollPositionActions.setValidationErrorPanels(panels));

    return { canSave, cantSaveMessageList };
  }
);

// probably redundant now
export const preUpdate = () => async () => {
  // const {  } = payload;
  // const paymentSuccess = preUpdateCheckPayment();
  // if (!paymentSuccess) {
  //   return false;
  // }

  // const staffSuccess = preUpdateCheckStaff(waitList);
  // if (!staffSuccess) {
  //   return false;
  // }

  // vm.booking.bookingLinks = vm.bookingLinks; // adding links information to the booking object before saving.

  // const covidGuestListSuccess = preUpdateCheckCovidGuestList();
  // if (!covidGuestListSuccess) {
  //   return false;
  // }

  // "Function Tracker" service option is always selected for the function tracker booking, so don't save the service.
  // if (!vm.booking.functionTracker) {
  //   vm.booking.serviceId = vm.service._id;
  // }

  // preUpdateSetStatus(newStatusName);
  // dispatch(setNonStandbyBookingType());

  return true;
};

const validationSlice = createSlice({
  name: 'validation', // used in action types
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },
    setNewBookingStaffRules: (
      state: IValidationState,
      action: PayloadAction<{ isEnabled: boolean; isMandatory: boolean }>
    ) => {
      state.staffNewBooking = action.payload;
    },
    setEditBookingStaffRules: (
      state: IValidationState,
      action: PayloadAction<{ isEnabled: boolean; isMandatory: boolean }>
    ) => {
      state.staffEditBooking = action.payload;
    },
    setStaffValue: (state: IValidationState, action: PayloadAction<string>) => {
      state.staffValue = action.payload;
    },
    setSaveAttempted: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.saveAttempted = action.payload;
    },
    setSaveSeatAttempted: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.saveAttempted = action.payload;
      state.saveNewWalkinAttempted = !action.payload;
      state.saveSeatAttempted = action.payload;
    },
    setSaveNewWalkinAttempted: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.saveAttempted = action.payload;
      state.saveSeatAttempted = !action.payload;
      state.saveNewWalkinAttempted = action.payload;
    },
    resetValidation: (state: IValidationState) => {
      state.saveAttempted = false;
      state.cantSaveMessageList = [];
      state.isBookedByValid = true;
      state.bookedByErrors = [];
      state.isPaymentPhoneValid = true;
      state.isPaymentEmailValid = true;
      state.isBookedByPaymentEmailValid = true;
      state.isBookedByPaymentPhoneValid = true;
      state.bookedByPaymentEmailErrorMsg = '';
      state.bookedByPaymentPhoneErrorMsg = '';
      state.validPhoneMessage = '';
      state.emailErrorMsg = '';
      state.isConfirmedSeat = false;
      state.isBookingExternallyUpdated = false;
    },
    setSaveStatus: (state: IValidationState, action: PayloadAction<SaveStatus>) => {
      state.saveStatus = action.payload;
    },
    setTableSeaterFullyLoaded: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.tableSeaterFullyLoaded = action.payload;
    },
    setEmailErrorMsg: (state: IValidationState, action: PayloadAction<string>) => {
      state.emailErrorMsg = action.payload;
    },
    setValidPhoneMessage: (state: IValidationState, action: PayloadAction<string>) => {
      state.validPhoneMessage = action.payload;
    },
    setPaymentPhone: (state: IValidationState, action: PayloadAction<string>) => {
      state.paymentPhone = action.payload;
    },
    setIsPaymentPhoneValid: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isPaymentPhoneValid = action.payload;
    },
    setPaymentEmail: (state: IValidationState, action: PayloadAction<string>) => {
      state.paymentEmail = action.payload;
    },
    setIsPaymentEmailValid: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isPaymentEmailValid = action.payload;
    },
    setIsPaymentPhoneUpdateChecked: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isPaymentPhoneUpdateChecked = action.payload;
    },
    setIsPaymentEmailUpdateChecked: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isPaymentEmailUpdateChecked = action.payload;
    },
    setBookedByPaymentPhone: (state: IValidationState, action: PayloadAction<string>) => {
      state.bookedByPaymentPhone = action.payload;
    },
    setIsBookedByPaymentPhoneValid: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isBookedByPaymentPhoneValid = action.payload;
    },
    setBookedByPaymentEmail: (state: IValidationState, action: PayloadAction<string>) => {
      state.bookedByPaymentEmail = action.payload;
    },
    setIsBookedByPaymentEmailValid: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isBookedByPaymentEmailValid = action.payload;
    },
    setBookedByPaymentEmailErrorMsg: (state: IValidationState, action: PayloadAction<string>) => {
      state.bookedByPaymentEmailErrorMsg = action.payload;
    },
    setBookedByPaymentPhoneErrorMsg: (state: IValidationState, action: PayloadAction<string>) => {
      state.bookedByPaymentPhoneErrorMsg = action.payload;
    },
    setIsBookedByValid: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isBookedByValid = action.payload;
    },
    setIsSavingBookedByEmail: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isSavingBookedByEmail = action.payload;
    },
    setIsSavingBookedByPhone: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isSavingBookedByPhone = action.payload;
    },
    setBookedByErrors: (state: IValidationState, action: PayloadAction<string[]>) => {
      state.bookedByErrors = action.payload;
    },
    setIsSavePaymentEmailToCustomer: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isSavePaymentEmailToCustomer = action.payload;
    },
    setIsSavePaymentPhoneNumberToCustomer: (
      state: IValidationState,
      action: PayloadAction<boolean>
    ) => {
      state.isSavePaymentPhoneNumberToCustomer = action.payload;
    },
    setBookedByValidation: (
      state: IValidationState,
      action: PayloadAction<IBookedByValidation>
    ) => {
      state.bookedByErrors = action.payload.bookedByErrors;
      state.isBookedByValid = action.payload.isBookedByValid;
      state.isBookedByEmailValid = action.payload.isBookedByEmailValid;
      state.isBookedByPhoneValid = action.payload.isBookedByPhoneValid;
    },
    setIsConfirmedSeat: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isConfirmedSeat = action.payload;
    },
    setIsBookingExternallyUpdated: (state: IValidationState, action: PayloadAction<boolean>) => {
      state.isBookingExternallyUpdated = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateCanSaveNewBooking.fulfilled, (state: IValidationState, action) => {
      state.canSave = action.payload.canSave;
      state.cantSaveMessageList = action.payload.cantSaveMessageList;
    });
    builder.addCase(updateCanSaveEditBooking.fulfilled, (state: IValidationState, action) => {
      state.canSave = action.payload.canSave;
      state.cantSaveMessageList = action.payload.cantSaveMessageList;
    });

    builder.addCase(
      saveNewBooking.fulfilled,
      (state: IValidationState, action: PayloadAction<SaveStatus>) => {
        if (action.payload === SaveStatus.SaveSuccessful) {
          state.saveAttempted = false;
        }
        state.saveStatus = action.payload;
      }
    );

    builder.addCase(
      saveEditBooking.fulfilled,
      (state: IValidationState, action: PayloadAction<SaveStatus>) => {
        if (action.payload === SaveStatus.SaveSuccessful) {
          state.saveAttempted = false;
        }
        state.saveStatus = action.payload;
      }
    );
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = validationSlice;
// Extract and export each action creator by name
export { actions as validationActions };
// Export the reducer, either as a default or named export
export default reducer;
